'use client'

import {
  LandingShell, Hero, SystemsStrip, FeatureGrid, HowItWorks, InControl, FaqSection, FinalCta, PRICE_ANSWER,
} from '@/components/landing/ExtensionLanding'

// Ad landing page for NHS applications (NHS Jobs, Trac, ApplyGateway).
// Claims here are limited to what was tested on those systems.

export default function NhsJobApplicationsPageClient() {
  return (
    <LandingShell>
      <Hero
        badge="Built for NHS applications"
        title="NHS applications,"
        highlight="without retyping your whole career."
        intro="NHS Jobs and Trac ask for every post, every qualification with its dates, two referees and an equality form on each application. CVCircuit fills them in from your Career Bank, and you check each page and press submit."
        example={{
          step: 'Employment history',
          label: 'Example of an NHS-style employment history section, filled in by CVCircuit',
          table: {
            columns: ['Employer', 'Job title', 'From', 'To'],
            rows: [
              ['Riverside Care Group', 'Senior Care Assistant', '03/2022', 'Present'],
              ['Hillview Pharmacy', 'Pharmacy Assistant', '09/2019', '02/2022'],
            ],
          },
          questions: [
            { q: 'Reason for leaving Hillview Pharmacy', a: 'Career progression' },
            { q: 'Please explain any gaps in your employment history', a: 'The explanation from your Career Bank' },
          ],
        }}
      />

      <SystemsStrip
        heading="Tested on the systems NHS employers use"
        systems={['NHS Jobs', 'Trac', 'ApplyGateway']}
        note="Many NHS trusts send you from NHS Jobs to Trac, and many care providers use ApplyGateway. CVCircuit has been tested on all three."
      />

      <FeatureGrid
        title="Everything an NHS form asks for,"
        highlight="from one Career Bank"
        intro="NHS application forms are long, and every one asks the same things again. Fill in your Career Bank once and each application gets the same accurate answers."
        items={[
          { title: 'Employment history', body: 'Every post with its dates, employer address, reason for leaving and notice period.' },
          { title: 'Gaps explained', body: "Gaps in your employment are explained from what you've written in your Career Bank, so every form says the same thing." },
          { title: 'Qualifications with dates', body: 'GCSEs, A-levels and degrees entered row by row, with grades and the start and end dates NHS forms ask for.' },
          { title: 'Referees', body: 'Names, contact details and how each referee knows you, for every form that asks for two.' },
          { title: 'Right to work', body: 'Right to work and visa questions answered the same way on every form, from what you set in your Career Bank.' },
          { title: 'Written answers (Pro)', body: 'Open questions get a first draft written from your own Career Bank, for you to edit against the person specification.' },
        ]}
      />

      <HowItWorks />
      <InControl
        items={[
          { title: 'Nothing is submitted for you', body: 'You check each section and press submit yourself. Nothing goes to the trust without you seeing it.' },
          { title: 'No made-up answers', body: "Answers come from your Career Bank. If it doesn't know something, the question is left for you rather than guessed." },
          { title: 'Your choices stay yours', body: "Declarations are only answered if you've switched them on in your Career Bank, and whether to apply under the Disability Confident scheme is always left for you." },
        ]}
      />

      <FaqSection
        faqs={[
          { q: 'Does it work on Trac?', a: "Yes. It has been tested on Trac, NHS Jobs' own application form and ApplyGateway. Sign in to the trust's system as usual, then let CVCircuit fill in each section." },
          { q: 'Will it write my supporting statement?', a: 'With Pro, it drafts answers to written questions from your Career Bank. Treat it as a first draft: read the person specification and make it your own before you submit.' },
          { q: 'What does it cost?', a: PRICE_ANSWER },
          { q: 'Does it submit applications for me?', a: 'No. CVCircuit fills in the form and you check every section before submitting it yourself.' },
          { q: 'Does it work on my phone?', a: "Not yet. It's a Chrome extension, so it needs Google Chrome on a laptop or desktop computer. On a phone, you can email yourself the link to open later on your computer." },
          { q: 'What happens to my data?', a: 'Your Career Bank is stored in your CVCircuit account and used to fill in the forms you choose. We never sell your data.' },
        ]}
      />

      <FinalCta
        title="Your next NHS application, without the retyping"
        body="Add CVCircuit to Chrome, set up your Career Bank once, and let it fill in the sections of your next NHS application."
      />
    </LandingShell>
  )
}
